import { Cmd } from "@typescript-tea/core";
import { exhaustiveCheck } from "ts-exhaustive-check";
import { PropertyValueSet } from "@genesys/property";
import { SettingsOverrides } from "../../../tools";
import { MoistureHeatLoadTypeEnum } from "../../../../graphql-types";
import { Action } from "./action";
import { State, UpdatedMoistureLoadSettings } from "./types";
import * as SharedState from "../../../../shared-state";
import * as SettingsOverride from "../../../components/settings-overrides";
import * as LoadGroup from "../load-group";

type ResultsInvalid = boolean;

export function update(
  action: Action,
  state: State,
  sharedState: SharedState.State,
  currentTopLevelCaseOverrides: SettingsOverride.SettingsOverrides,
  currentTopLevelMoistureLoadSettings: PropertyValueSet.PropertyValueSet
): readonly [
  State,
  UpdatedMoistureLoadSettings,
  SettingsOverrides,
  Cmd<Action>?,
  ResultsInvalid?,
  ReadonlyArray<SharedState.Action | undefined>?
] {
  switch (action.type) {
    case "addNewLoadToGroup": {
      const currentGroupState = state.loadGroupState[action.load.sourceName];
      const groupName = action.load.sourceName;

      if (!currentGroupState) {
        const loadGroupState = {
          ...state.loadGroupState
        };
        const newLoadGroupState = LoadGroup.init(
          groupName,
          0, // TODO
          [],
          state.moistureLoadClaimValue!,
          state.propertyDefinitions!
        );

        loadGroupState[groupName] = LoadGroup.addLoadToGroup(
          action.load,
          newLoadGroupState,
          sharedState,
          currentTopLevelCaseOverrides,
          currentTopLevelMoistureLoadSettings,
          state.defaultSettings!
        );

        return [
          {
            ...state,
            selectedLoadGroup: newLoadGroupState.group,
            loadGroupState
          },
          currentTopLevelMoistureLoadSettings,
          currentTopLevelCaseOverrides,
          undefined,
          true
        ];
      }

      const newLoadGroupState = LoadGroup.addLoadToGroup(
        action.load,
        currentGroupState,
        sharedState,
        currentTopLevelCaseOverrides,
        currentTopLevelMoistureLoadSettings,
        state.defaultSettings!
      );

      return [
        {
          ...state,
          selectedLoadGroup: newLoadGroupState.group,
          loadGroupState: {
            ...state.loadGroupState,
            [groupName]: newLoadGroupState
          }
        },
        currentTopLevelMoistureLoadSettings,
        currentTopLevelCaseOverrides,
        undefined,
        true
      ];
    }

    case "changePageTab": {
      return [
        {
          ...state,
          currentPageTab: action.tab
        },
        currentTopLevelMoistureLoadSettings,
        currentTopLevelCaseOverrides
      ];
    }

    case "dispatchLoadGroup": {
      const stateForGroup = state.loadGroupState[action.group]!;
      const [
        newGroupState,
        resultisInvalid,
        loadGroupCmd,
        loadGroupSharedStateActions
      ] = LoadGroup.update(
        action.action,
        stateForGroup,
        sharedState,
        currentTopLevelCaseOverrides,
        currentTopLevelMoistureLoadSettings,
        state.defaultSettings!
      );

      if (Object.entries(newGroupState.activeLoads).length === 0) {
        const loadGroupStates = {
          ...state.loadGroupState
        };
        delete loadGroupStates[action.group];
        return [
          {
            ...state,
            selectedLoadGroup: undefined,
            loadGroupState: loadGroupStates,
            currentPageTab: "moisture-heat-load-types"
          },
          currentTopLevelMoistureLoadSettings,
          currentTopLevelCaseOverrides,
          Cmd.map(
            subaction => Action.dispatchLoadGroup(action.group, subaction),
            loadGroupCmd
          ),
          resultisInvalid,
          loadGroupSharedStateActions
        ];
      }

      return [
        {
          ...state,
          loadGroupState: {
            ...state.loadGroupState,
            [action.group]: newGroupState
          }
        },
        currentTopLevelMoistureLoadSettings,
        currentTopLevelCaseOverrides,
        Cmd.map(
          subaction => Action.dispatchLoadGroup(action.group, subaction),
          loadGroupCmd
        ),
        resultisInvalid,
        loadGroupSharedStateActions
      ];
    }

    case "setSelectedLoadGroup": {
      return [
        {
          ...state,
          selectedLoadGroup: action.selectedLoadGroup
        },
        currentTopLevelMoistureLoadSettings,
        currentTopLevelCaseOverrides
      ];
    }

    case "setViewMode": {
      return [
        {
          ...state,
          viewModeState: { ...state.viewModeState, viewMode: action.viewMode }
        },
        currentTopLevelMoistureLoadSettings,
        currentTopLevelCaseOverrides
      ];
    }

    // case "updateAllLoads": {
    //   const activeLoadGroups = Object.keys(state.loadGroupState);
    //   const updatedLoadGroups = activeLoadGroups.reduce((acc, key) => {
    //     const currentGroupState =
    //       state.loadGroupState[key as MoistureHeatLoadTypeEnum];
    //     const newGroupState = LoadGroup.updateAllLoads(
    //       currentGroupState,
    //       action.newValues,
    //       action.loadProperties
    //     );

    //     acc[key as MoistureHeatLoadTypeEnum] = newGroupState;

    //     return acc;
    //   }, {} as Record<MoistureHeatLoadTypeEnum, LoadGroup.State>);
    //   return [
    //     {
    //       ...state,
    //       loadGroupState: {
    //         ...state.loadGroupState,
    //         ...updatedLoadGroups
    //       }
    //     },
    //     currentTopLevelMoistureLoadSettings,
    //     currentTopLevelCaseOverrides
    //   ];
    // }

    default:
      return exhaustiveCheck(action, true);
  }
}

// export function getUpdatedSelectedLoadState(
//   state: State,
//   updatedSettings: PropertyValueSet.PropertyValueSet
// ): LoadsState {
//   return LocalTools.getSelectedLoadState(state, updatedSettings);

//   // return {
//   //   ...state,
//   //   loadsState: {
//   //     ...selectedLoadsState
//   //   }
//   // };
// }

export function forceUpdateAllLoads(
  state: State,
  prevValues: PropertyValueSet.PropertyValueSet,
  newValues: PropertyValueSet.PropertyValueSet,
  allLoads: LoadGroup.LoadDef[]
) {
  const activeLoadGroups = Object.keys(state.loadGroupState);
  const updatedLoadGroups = activeLoadGroups.reduce((acc, key) => {
    const currentGroupState =
      state.loadGroupState[key as MoistureHeatLoadTypeEnum];
    const loadDef = allLoads.find(x => x.sourceName === key)!;
    const newGroupState = LoadGroup.updateAllLoads(
      currentGroupState,
      prevValues,
      newValues,
      loadDef
    );

    acc[key as MoistureHeatLoadTypeEnum] = newGroupState;

    return acc;
  }, {} as Record<MoistureHeatLoadTypeEnum, LoadGroup.State>);
  return {
    ...state,
    loadGroupState: {
      ...state.loadGroupState,
      ...updatedLoadGroups
    }
  };
}

export function getUpdatedResultState(
  state: State,
  removeResult: boolean
): State {
  return {
    ...state,
    ...(removeResult && {
      currentPageTab: "moisture-heat-load-types"
    })
  };
}

type LoadStateWithGroupName = LoadGroup.ActiveLoadState & {
  readonly groupName: MoistureHeatLoadTypeEnum;
};

export function getCurrentSelectedLoads(state: State) {
  return Object.keys(state.loadGroupState).reduce((acc, key) => {
    const groupState = state.loadGroupState[key as MoistureHeatLoadTypeEnum];
    const groupLoads = LoadGroup.getAllActiveLoads(groupState).map(x => ({
      ...x,
      groupName: key as MoistureHeatLoadTypeEnum
    }));

    return acc.concat(groupLoads);
  }, [] as Array<LoadStateWithGroupName>);
}

export function setSelectedLoadGroup(
  state: State,
  group: MoistureHeatLoadTypeEnum | undefined
): State {
  return {
    ...state,
    selectedLoadGroup: group
  };
}

export function updateForApplicationTypeChange(
  state: State,
  updatedProperties: PropertyValueSet.PropertyValueSet,
  allLoads: LoadGroup.LoadDef[],
  sharedState: SharedState.State,
  defaultSettings: PropertyValueSet.PropertyValueSet,
  currentTopLevelCaseOverrides: SettingsOverrides
): State {
  return {
    ...state,
    ...updateLoadExistenceFromApplicationSettings(
      state,
      updatedProperties,
      allLoads,
      sharedState,
      defaultSettings,
      currentTopLevelCaseOverrides
    )
  };
}

function updateLoadExistenceFromApplicationSettings(
  state: State,
  settings: PropertyValueSet.PropertyValueSet,
  allLoads: LoadGroup.LoadDef[],
  sharedState: SharedState.State,
  defaultSettings: PropertyValueSet.PropertyValueSet,
  currentTopLevelCaseOverrides: SettingsOverrides
): State {
  const loadsThatShouldBeActive = getActiveLoadsFromSettings(
    allLoads,
    settings
  );

  const currentLoadGroups = Object.keys(state.loadGroupState);

  const newLoadGroups = Object.keys(MoistureHeatLoadTypeEnum).reduce(
    (soFar, current) => {
      if (
        loadsThatShouldBeActive.includes(current as MoistureHeatLoadTypeEnum)
      ) {
        if (currentLoadGroups.includes(current as MoistureHeatLoadTypeEnum)) {
          return soFar;
        }
        let newLoadGroupState = LoadGroup.init(
          current as MoistureHeatLoadTypeEnum,
          0, // TODO,
          [],
          state.moistureLoadClaimValue!,
          state.propertyDefinitions!
        );

        newLoadGroupState = LoadGroup.addLoadToGroup(
          allLoads.find(x => x.sourceName === current)!,
          newLoadGroupState,
          sharedState,
          currentTopLevelCaseOverrides,
          settings,
          defaultSettings
        );

        soFar[current as MoistureHeatLoadTypeEnum] = newLoadGroupState;

        return soFar;
      } else {
        if (!currentLoadGroups.includes(current as MoistureHeatLoadTypeEnum)) {
          return soFar;
        }
        delete soFar[current as MoistureHeatLoadTypeEnum];

        return soFar;
      }
    },
    state.loadGroupState as Record<MoistureHeatLoadTypeEnum, LoadGroup.State>
  );

  return {
    ...state,
    loadGroupState: {
      ...newLoadGroups
    }
  };
}

function getActiveLoadsFromSettings(
  loadsDefMap: ReadonlyArray<LoadGroup.LoadDef>,
  moistureLoadSettings: PropertyValueSet.PropertyValueSet
) {
  return loadsDefMap
    .filter(x => {
      const hasHeat = x.additionalInfo.isHeatAdded(moistureLoadSettings);
      const hasMoist = x.additionalInfo.isMoistureAdded(moistureLoadSettings);

      return hasHeat || hasMoist;
    })
    .map(x => x.sourceName);
}
